/**
 * KEMI PARIS — lander sticky CTA bar.
 *
 * Generalises the pattern proven in assets/sticky-add-to-cart.js (which is
 * PDP-bound: it binds to [data-ref="product-form"]). This one is lander-generic.
 *
 * Rules it enforces:
 *  - Reveal only after the first screen is gone (viewport-relative, not a magic px).
 *  - Hide while the real offer box is in view: never compete with, or cover, the
 *    actual conversion point.
 *  - Hide over the footer so it never covers the policy/trust links.
 *  - Stay out of the a11y tree while hidden (inert), so keyboard users don't tab
 *    into an invisible CTA (WCAG 2.4.3).
 *  - Reserve scroll-padding-bottom equal to its own height so a focused element
 *    can never end up underneath it (WCAG 2.2 SC 2.4.11, failure technique F110).
 *  - Dismissible, and the dismissal sticks for the session.
 */
const DISMISS_KEY = 'kemi:sticky-cta:dismissed';

class KemiStickyCta extends HTMLElement {
  constructor() {
    super();
    this.offer = null;
    this.footer = null;
    this.offerInView = false;
    this.footerInView = false;
    this.scrolledPast = false;
    this.dismissed = false;
    this.observers = [];
    this.ticking = false;
    this.onScroll = this.onScroll.bind(this);
    this.onDismiss = this.onDismiss.bind(this);
  }

  connectedCallback() {
    if (this.dataset.init === 'true') return;
    this.dataset.init = 'true';

    try {
      this.dismissed = sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (e) {
      this.dismissed = false;
    }
    if (this.dismissed) {
      this.remove();
      return;
    }

    this.offer = document.querySelector(this.dataset.offerSelector || '#offer');
    this.footer = document.querySelector('[data-ref="footer"]') || document.querySelector('footer');

    this.querySelector('[data-sticky-cta-dismiss]')?.addEventListener('click', this.onDismiss);

    this.#initObservers();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    this.observers.forEach((o) => o.disconnect());
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    document.documentElement.style.removeProperty('--kemi-sticky-cta-height');
  }

  #initObservers() {
    if (this.offer) {
      // Keep the CTA available until the real buy box is genuinely on screen,
      // not the instant its top edge peeks up from the bottom. The offer box is
      // often taller than the viewport, so a raw threshold hides the bar a full
      // screen before the offer is even readable. Shrinking the root's bottom by
      // 35% means "in view" == the offer has risen into the upper ~65% of the
      // screen, i.e. the shopper can actually see and use it.
      const offerObserver = new IntersectionObserver(
        ([entry]) => {
          this.offerInView = entry.isIntersecting;
          this.#update();
        },
        { rootMargin: '0px 0px -35% 0px' }
      );
      offerObserver.observe(this.offer);
      this.observers.push(offerObserver);
    }

    if (this.footer) {
      const footerObserver = new IntersectionObserver(([entry]) => {
        this.footerInView = entry.isIntersecting;
        this.#update();
      });
      footerObserver.observe(this.footer);
      this.observers.push(footerObserver);
    }
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      // Viewport-relative: "the first screen is gone". A raw px threshold means
      // something different on a 667px phone than on a 1440px desktop.
      const vh = window.innerHeight || 800;
      const floor = parseInt(this.dataset.revealFloor, 10) || 0;
      const multiple = parseFloat(this.dataset.revealVh) || 1;
      const threshold = Math.max(floor, vh * multiple);
      const past = window.scrollY > threshold;
      if (past === this.scrolledPast) return;
      this.scrolledPast = past;
      this.#update();
    });
  }

  onDismiss() {
    this.dismissed = true;
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {
      /* private mode: dismissal just won't persist */
    }
    this.#setActive(false);
    setTimeout(() => this.remove(), 300);
  }

  #update() {
    const shouldBeActive = this.scrolledPast && !this.offerInView && !this.footerInView && !this.dismissed;
    this.#setActive(shouldBeActive);
  }

  #setActive(active) {
    if (this.dataset.active === String(active)) return;
    this.dataset.active = String(active);

    // inert + aria-hidden in lockstep with visibility. A transform alone leaves
    // the bar in the tab order while it is off-screen.
    if (active) {
      this.removeAttribute('inert');
      this.removeAttribute('aria-hidden');
      requestAnimationFrame(() => {
        const h = this.offsetHeight;
        if (h) document.documentElement.style.setProperty('--kemi-sticky-cta-height', `${h}px`);
      });
    } else {
      this.setAttribute('inert', '');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.style.removeProperty('--kemi-sticky-cta-height');
    }
  }
}

if (!customElements.get('kemi-sticky-cta')) {
  customElements.define('kemi-sticky-cta', KemiStickyCta);
}
